import React, { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";

/**
 * MarkdownSectionEditor
 *
 * Props:
 * - initialMarkdown: string
 * - onSave?: async function(markdown)
 * - title?: string
 * - readOnly?: boolean
 * - editorHeight?: string (CSS value like "320px" or "40vh", default "320px")
 */
export default function MarkdownSectionEditor({
  initialMarkdown = "",
  onSave,
  title = "Opening section",
  readOnly = true,
  editorHeight = "320px",
  showSaveButton = true
}) {
  
  const [markdown, setMarkdown] = useState(initialMarkdown);
  const [editing, setEditing] = useState(readOnly);
  const [tab, setTab] = useState("write"); // 'write' | 'preview'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isEditable = showSaveButton ? editing : true;

  useEffect(() => {
    setMarkdown(initialMarkdown);
  }, [initialMarkdown]);
  async function handleSave() {
    setError(null);
    setLoading(true);
    try {
      if (onSave) await onSave(markdown);
      setEditing(false);
    } catch (err) {
      setError(err?.message ?? String(err));
    } finally {
      setLoading(false);
    }
  }

  function handleCancel() {
    setMarkdown(initialMarkdown);
    setEditing(false);
    setError(null);
  }

  // shared pane style
  const paneStyle = {
    height: editorHeight,
    minHeight: editorHeight,
    maxHeight: editorHeight,
    width: "100%",
    boxSizing: "border-box",
    overflow: "auto",
    background: "#f8fafc", // light grey
  };

  return (
    <div style={{ margin: "0px auto", fontFamily: "Inter, Roboto, sans-serif" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        {showSaveButton && (
        <div style={{ fontSize: 18, fontWeight: 800 }}>{title}</div>)}
        {showSaveButton && (
        <div>
          {!editing ? (
            <button
              onClick={() => {
                setEditing(true);
                setTab("write");
              }}
              style={{
                background: "#5b21b6",
                color: "white",
                padding: "8px 24px",
                borderRadius: 8,
                border: "none",
              }}
            >
              Edit
            </button>
          ) : (
            <>
              <button
                onClick={handleCancel}
                disabled={loading}
                style={{
                  marginRight: 8,
                  padding: "8px 24px",
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  background: "white",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                style={{
                  background: "#7c3aed",
                  color: "white",
                  padding: "8px 24px",
                  borderRadius: 8,
                  border: "none",
                }}
              >
                {loading ? "Saving..." : "Save"}
              </button>
            </>
          )}
        </div>
        )}
      </div>

      {/* Card container */}
      <div
        
      >
        {isEditable ? (
          <>
            {/* Tabs */}
            <div
              style={{
                display: "flex",
                gap: 0,
                marginBottom: 0,
              }}
            >
              <button
                onClick={() => setTab("write")}
                aria-pressed={tab === "write"}
                style={{
                  padding: "8px 32px",
                  border: tab === "write" ? "1px solid #d1d5db": "0px",
                  borderBottom:
                    tab === "write"
                      ? "0px  transparent"
                      : "1px solid #d1d5db",
                  borderRadius: tab === "write" ? "6px 6px 0px 0px" : "0px",
                  background: tab === "write" ? "#f8fafc" : "#ffffff",
                  color: "#0f172a",
                  cursor: "pointer",
                }}
              >
                Write
              </button>
              <button
                onClick={() => setTab("preview")}
                aria-pressed={tab === "preview"}
                style={{
                  padding: "8px 32px",
                  border: tab === "preview" ? "1px solid #d1d5db": "0px",
                  borderBottom:
                    tab === "preview"
                      ? "0px  transparent"
                      : "1px solid #d1d5db",
                  borderRadius: tab === "preview" ? "6px 6px 0px 0px" : "0px",
                  background: tab === "preview" ? "#f8fafc" : "#ffffff",
                  color: "#0f172a",
                  cursor: "pointer",
                }}
              >
                Preview
              </button>
              <div style={{width: "100%", borderBottom: "1px solid #d1d5db"}}>

              </div>
            </div>

            {/* Editor / Preview panes */}
            {tab === "write" ? (
              <textarea
                value={markdown}
                onChange={(e) => {
                  const value = e.target.value;
                  setMarkdown(value);
              
                  if (!showSaveButton && onSave) {
                    onSave(value); // live update
                  }
                }}
                style={{
                  ...paneStyle,
                  padding: 24,
                  margin: "0px",
                  borderRadius: "0 0 6px 6px",
                  border: "1px solid #d1d5db",
                  borderTop: "none",
                  resize: "none",
                  fontFamily: "inherit",
                  fontSize: 18,
                  color: "#0f172a",
                }}
              />
            ) : (
              <div
                style={{
                  ...paneStyle,
                  padding: 24,
                  borderRadius: "0 0 6px 6px",
                  border: "1px solid #d1d5db",
                  borderTop: "none",
                }}
                className="markdown-preview"
              >
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeSanitize]}
                >
                  {markdown || "_Nothing to preview_"}
                </ReactMarkdown>
              </div>
            )}

            {error && (
              <div style={{ color: "crimson", marginTop: 8 }}>{error}</div>
            )}
            <div style={{ marginTop: 10, fontSize: 13, color: "#6b7280" }}>
              Tip: Use Markdown in the Write tab. Switch to Preview to see the
              rendered result.
            </div>
          </>
        ) : (
          // Read-only preview
          <div 
          style={{
            lineHeight: 1.6,
            border: "1px solid #e6e6e6",
            borderRadius: 10,
            padding: 22,
            background: "#fff",
          }} className="markdown-preview mt-4">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeSanitize]}
            >
              {markdown}
            </ReactMarkdown>
          </div>
        )}
      </div>

      {/* Styling for rendered markdown */}
      <style>{`
        .markdown-preview h1 { font-size: 1.5rem; margin: 0.75rem 0; font-weight: 700; }
        .markdown-preview h2 { font-size: 1.25rem; margin: 0.75rem 0; font-weight: 600; }
        .markdown-preview h3 { font-size: 1.1rem; margin: 0.5rem 0; font-weight: 600; }
        .markdown-preview p { margin: 0.5rem 0; color: #0f172a; }
        .markdown-preview ul { margin: 0.5rem 0 0.5rem 1.25rem; list-style-type: disc; }
        .markdown-preview li { margin: 0.25rem 0; }
        .markdown-preview strong { font-weight: 700; }
        .markdown-preview em { font-style: italic; }
        .markdown-preview blockquote {
          border-left: 4px solid #d1d5db;
          padding-left: 12px;
          margin: 0.75rem 0;
          color: #374151;
          font-style: italic;
          background: #f1f5f9;
          border-radius: 4px;
        }
      `}</style>
    </div>
  );
}
